/**
 * Traffic-key resolution and factory-config selection for Harness flags.
 */
import { loadRuntimeConfig } from "@/config/runtimeConfig";
import { createLocalhostConfig, type SplitClientConfig } from "./splitClient";

export const ANONYMOUS_TRAFFIC_KEY = "anonymous";

type TrafficKeySource = {
  selectedAccount?: { uuid?: string } | null;
  selectedMsp?: { uuid?: string } | null;
};

/**
 * Resolves the Split traffic key (targeting identity): the selected account/org
 * uuid, then the selected MSP uuid, falling back to an anonymous key before a
 * user context exists.
 */
export const resolveTrafficKey = (auth: TrafficKeySource): string =>
  auth.selectedAccount?.uuid ?? auth.selectedMsp?.uuid ?? ANONYMOUS_TRAFFIC_KEY;

/** The client-side Harness SDK key from runtime config, if configured. */
export const getHarnessSdkKey = (): string | undefined => {
  try {
    return loadRuntimeConfig().VITE_HARNESS_SDK_KEY;
  } catch {
    return undefined;
  }
};

/**
 * Builds the Split factory config for a traffic key. Without a configured SDK
 * key (local dev / tests) it falls back to localhost mock mode.
 */
export const createHarnessConfig = (key: string): SplitClientConfig => {
  const sdkKey = getHarnessSdkKey();
  if (!sdkKey) return createLocalhostConfig(key);
  return { authorizationKey: sdkKey, key };
};
